import React, { useState } from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { DocumentsParamList } from '../../../typings/Params';
import Filters from '../../../components/Filter/FilterIndex/FilterIndex';
import { translate } from '../../../services/translate';
import { useAuth } from '../../../context/Auth';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import moment from 'moment';
import FilterDependencyMessage from '../../../components/Filters/FilterDependencyMessage';
import CollapsibleFilter from '../../../components/Filters/Dispatch/CollapsibleFilter';
import ActingBodyUnitsList from '../../../components/Filters/Dispatch/ActingBodyUnitsList';
import ActingBodiesList from '../../../components/Filters/Dispatch/ActingBodiesList';
import FilterPeriodSelector from '../../../components/Filters/Dispatch/FilterPeriodSelector';
import DocumentUsersList from '../../../components/Filters/Documents/DocumentUsersList';

interface Props {
    filters: DocumentsParamList['DocumentsList'];
    setFilters: React.Dispatch<React.SetStateAction<DocumentsParamList['DocumentsList']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

export default function Filter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const navigation = useNavigation<NavigationProp<DocumentsParamList, 'DocumentsList'>>();
    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [selectedActingBodyId, setSelectedActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [selectedActingBodyUnitIds, setSelectedActingBodyUnitIds] = useState<number[]>(filters.actingBodyUnitIds ?? []);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>(filters.userIds ?? []);
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.createdFrom ? new Date(filters.createdFrom) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.createdFrom ? moment(filters.createdFrom).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.createdTo ? new Date(filters.createdTo) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.createdTo ? moment(filters.createdTo).format('HH:mm') : '');
    const [selectedPreset, setSelectedPreset] = useState<string>('');

    const [openFilter, setOpenFilter] = useState<string | null>(null);

    const actingBodyId = getActingBodyId();
    const needsActingBody = actingBodyId === 0;

    const totalActiveFilters = [
        selectedActingBodyId !== 0 ? 1 : 0,
        selectedActingBodyUnitIds.length > 0 ? 1 : 0,
        (selectedPreset || beginInDateFilter || endInDateFilter) ? 1 : 0,
        selectedUserIds.length > 0 ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.documents_manage_all_acting_bodies_document_category) {
            return selectedActingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    const toggleFilter = (key: string) => {
        setOpenFilter(prev => prev === key ? null : key);
    };

    const handleCleanFilter = () => {
        setOpenFilter(null);
        setTextFilter('');
        setSelectedActingBodyId(0);
        setSelectedActingBodyUnitIds([]);
        setSelectedUserIds([]);
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setSelectedPreset('');
    };

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const createdFrom = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const createdTo = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        const params = {
            page: 1 as const,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            actingBodyUnitIds: selectedActingBodyUnitIds.length > 0 ? selectedActingBodyUnitIds : undefined,
            userIds: selectedUserIds.length > 0 ? selectedUserIds : undefined,
            createdFrom: createdFrom && !isNaN(createdFrom) ? createdFrom : undefined,
            createdTo: createdTo && !isNaN(createdTo) ? createdTo : undefined,
        };

        navigation.setParams({
            ...filters,
            ...params,
        });

        setFilters({
            ...filters,
            ...params,
            page: 0,
        });
    };

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter} filterCount={totalActiveFilters}>

            <MyAppText style={styles.sectionTitle(0)}>
                {translate('organizational')}
            </MyAppText>

            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.documents_manage_all_acting_bodies_documents) &&
                <CollapsibleFilter
                    label={translate('actingBody')}
                    isOpen={openFilter === 'actingBody'}
                    onToggle={() => toggleFilter('actingBody')}
                    count={selectedActingBodyId !== 0 ? 1 : 0}
                >
                    <ActingBodiesList value={selectedActingBodyId} setValue={setSelectedActingBodyId} />
                </CollapsibleFilter>
            }

            <CollapsibleFilter
                label={translate('actingBodyUnit')}
                isOpen={openFilter === 'actingBodyUnit'}
                onToggle={() => toggleFilter('actingBodyUnit')}
                count={selectedActingBodyUnitIds.length}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <ActingBodyUnitsList value={selectedActingBodyUnitIds} setValue={setSelectedActingBodyUnitIds} actingBodyId={actingBodyId} />
                }
            </CollapsibleFilter>

            <MyAppText style={styles.sectionTitle()}>
                {translate('actions')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('createdAt')}
                isOpen={openFilter === 'createdAt'}
                onToggle={() => toggleFilter('createdAt')}
                count={(selectedPreset || beginInDateFilter || endInDateFilter) ? 1 : 0}
            >
                <FilterPeriodSelector
                    preset={selectedPreset}
                    setPreset={(preset, range) => {
                        setSelectedPreset(preset);
                        if (range) {
                            setBeginInDateFilter(new Date(range.beginIn));
                            setBeginInHourFilter(moment(range.beginIn).format('HH:mm'));
                            setEndInDateFilter(new Date(range.endIn));
                            setEndInHourFilter(moment(range.endIn).format('HH:mm'));
                        } else if (preset === '') {
                            setBeginInDateFilter(null);
                            setBeginInHourFilter('');
                            setEndInDateFilter(null);
                            setEndInHourFilter('');
                        }
                    }}
                    beginDate={beginInDateFilter}
                    setBeginDate={setBeginInDateFilter}
                    beginTime={beginInHourFilter}
                    setBeginTime={setBeginInHourFilter}
                    endDate={endInDateFilter}
                    setEndDate={setEndInDateFilter}
                    endTime={endInHourFilter}
                    setEndTime={setEndInHourFilter}
                    onClear={() => {
                        setSelectedPreset('');
                        setBeginInDateFilter(null);
                        setBeginInHourFilter('');
                        setEndInDateFilter(null);
                        setEndInHourFilter('');
                    }}
                    clearPreset={() => setSelectedPreset('')}
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('user')}
                isOpen={openFilter === 'user'}
                onToggle={() => toggleFilter('user')}
                count={selectedUserIds.length}
            >
                {needsActingBody
                    ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                    : <DocumentUsersList
                        value={selectedUserIds}
                        setValue={setSelectedUserIds}
                        actingBodyId={actingBodyId}
                        actingBodyUnitIds={selectedActingBodyUnitIds}
                    />
                }
            </CollapsibleFilter>
        </Filters>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    sectionTitle: (padding: number = 16) => ({
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500'],
        paddingTop: padding,
        textTransform: 'uppercase',
    }),
}));
